/*
 * Sprint 4.11.24 — Issued Statement (Member / Client / Vendor)
 *
 * Single-page UI that lets the operator print a "Material Issued" or
 * "Asset Issued" PDF against ONE party (Member / Client / Vendor).
 *
 *   ┌──────────────────────────────────────────┐
 *   │  Party Type [Member ▾]  Party [Picker ▾] │
 *   │  Branch     [All ▾]     From [date]      │
 *   │  Kind  ⦿ Material  ◯ Asset               │
 *   │  [ Preview ]  [ Download PDF ]           │
 *   ├──────────────────────────────────────────┤
 *   │  Preview table (last fetched run)        │
 *   └──────────────────────────────────────────┘
 *
 * PDF endpoint streams the file with the SI-themed builder.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Card } from "../components/ui/card";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Printer, Eye, Search, X, User2, Building2, Briefcase, FileText } from "lucide-react";

const PARTY_TYPES = [
  { id: "Member",  label: "Member",  icon: User2 },
  { id: "Client",  label: "Client",  icon: Briefcase },
  { id: "Vendor",  label: "Vendor",  icon: Building2 },
];

function fmtINR(n) {
  return Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function IssuedStatementPage() {
  const { activeBranch } = useAuth();
  const [partyType, setPartyType] = useState("Member");
  const [partyQuery, setPartyQuery] = useState("");
  const [partyResults, setPartyResults] = useState([]);
  const [party, setParty] = useState(null); // selected {id,name,code,...}
  const [kind, setKind] = useState("material"); // material | asset
  const [branchId, setBranchId] = useState("");
  const [branches, setBranches] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  // Load branches
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/branches");
        setBranches(r.data || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // Live-search party as user types (debounced)
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/reports/party-issued/parties", {
          params: { party_type: partyType, q: partyQuery, branch_id: branchId },
        });
        if (!cancelled) setPartyResults(r.data || []);
      } catch (e) {
        if (!cancelled) setPartyResults([]);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [partyType, partyQuery, branchId]);

  // When party_type changes, clear the picked party
  useEffect(() => { setParty(null); setPartyQuery(""); }, [partyType]);

  const canRun = !!party && !!kind;

  const runPreview = async () => {
    if (!canRun) { toast.error("Pick a party first"); return; }
    setBusy(true);
    try {
      const r = await api.get("/reports/party-issued/preview", {
        params: {
          party_type: partyType, party_id: party.id, kind,
          from_date: fromDate, to_date: toDate, branch_id: branchId,
        },
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    if (!canRun) { toast.error("Pick a party first"); return; }
    setBusy(true);
    try {
      const r = await api.get("/reports/party-issued.pdf", {
        params: {
          party_type: partyType, party_id: party.id, kind,
          from_date: fromDate, to_date: toDate, branch_id: branchId,
        },
        responseType: "blob",
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const label = kind === "material" ? "Material" : "Asset";
      const safe = (party.name || party.id).replace(/[\\/]/g, "-").slice(0, 60);
      a.download = `${label}-Issued-${safe}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Could not generate PDF");
    } finally { setBusy(false); }
  };

  const totalsRow = preview && (
    <div className="flex flex-wrap items-center gap-4 px-4 py-2 bg-slate-50 border-y border-slate-200 text-xs">
      <span><b>Lines:</b> {preview.totals?.line_count || 0}</span>
      {kind === "material" && (
        <span><b>Total Qty:</b> {fmtINR(preview.totals?.total_qty)}</span>
      )}
      <span><b>Total Value:</b> ₹ {fmtINR(preview.totals?.total_value)}</span>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">REPORT</div>
        <h1 className="font-display text-3xl font-bold">Issued Statement</h1>
        <p className="text-xs text-slate-500 mt-1">
          Print a PDF of every Issue Voucher line attributed to one Member, Client, or Vendor — themed like the Sales Invoice print.
        </p>
      </div>

      <Card className="p-4 space-y-4" data-testid="issued-statement-card">
        {/* Row 1: Party Type radios */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">1. Pick Party Type</div>
          <div className="flex gap-2">
            {PARTY_TYPES.map(pt => {
              const Icon = pt.icon;
              const active = partyType === pt.id;
              return (
                <button key={pt.id} data-testid={`party-type-${pt.id.toLowerCase()}`}
                  onClick={() => setPartyType(pt.id)}
                  className={`flex items-center gap-2 px-4 py-2 border text-xs uppercase tracking-wider font-bold ${
                    active
                      ? "bg-[#0B1F3A] border-[#0B1F3A] text-white"
                      : "bg-white border-slate-300 text-slate-700 hover:border-[#F97316]"
                  }`}>
                  <Icon size={14} /> {pt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Row 2: Party search + filters */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3">
          <div className="lg:col-span-5">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">
              2. Search & Select {partyType}
            </div>
            <div className="relative">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="party-search-input" type="text"
                value={partyQuery}
                onChange={e => setPartyQuery(e.target.value)}
                placeholder={`Type ${partyType} name / code / phone…`}
                className="w-full pl-7 pr-7 py-2 text-sm border border-slate-300 focus:border-[#F97316] focus:outline-none" />
              {partyQuery && (
                <button onClick={() => setPartyQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                  <X size={12} />
                </button>
              )}
            </div>
            {/* Results dropdown */}
            {(partyQuery || partyResults.length > 0) && !party && (
              <div className="border border-slate-200 max-h-60 overflow-y-auto mt-1">
                {partyResults.length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500 italic">No matches</div>
                )}
                {partyResults.map(p => (
                  <button key={p.id} data-testid={`party-pick-${p.id}`}
                    onClick={() => { setParty(p); setPartyResults([]); setPartyQuery(""); }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 border-b border-slate-100 last:border-b-0">
                    <div className="font-bold text-slate-800">{p.name}</div>
                    <div className="text-slate-500 text-[10px] mt-0.5">
                      {p.code && `${p.code}`}
                      {p.designation && ` · ${p.designation}`}
                      {p.phone && ` · ${p.phone}`}
                      {p.branch_name && ` · ${p.branch_name}`}
                      {p.gst_number && ` · ${p.gst_number}`}
                    </div>
                  </button>
                ))}
              </div>
            )}
            {/* Selected party chip */}
            {party && (
              <div data-testid="selected-party-chip"
                className="mt-2 inline-flex items-center gap-2 bg-orange-50 border border-[#F97316] px-3 py-1.5 text-xs">
                <span className="font-bold text-slate-800">{party.name}</span>
                {party.code && <span className="text-slate-500 font-mono">{party.code}</span>}
                <button onClick={() => setParty(null)} className="text-slate-400 hover:text-rose-600 ml-1"
                  data-testid="clear-party-btn">
                  <X size={12} />
                </button>
              </div>
            )}
          </div>

          <div className="lg:col-span-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">3. Kind</div>
            <div className="flex gap-1">
              <button data-testid="kind-material"
                onClick={() => setKind("material")}
                className={`flex-1 px-3 py-2 text-xs uppercase font-bold tracking-wider border ${
                  kind === "material" ? "bg-[#F97316] border-[#F97316] text-white" : "bg-white border-slate-300 text-slate-700"
                }`}>
                Material
              </button>
              <button data-testid="kind-asset"
                onClick={() => setKind("asset")}
                className={`flex-1 px-3 py-2 text-xs uppercase font-bold tracking-wider border ${
                  kind === "asset" ? "bg-[#F97316] border-[#F97316] text-white" : "bg-white border-slate-300 text-slate-700"
                }`}>
                Asset
              </button>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">From Date</div>
            <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
              data-testid="from-date" className="w-full px-2 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none" />
          </div>
          <div className="lg:col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">To Date</div>
            <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
              data-testid="to-date" className="w-full px-2 py-2 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none" />
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Branch (optional)</div>
          <select value={branchId} onChange={e => setBranchId(e.target.value)} data-testid="branch-select"
            className="px-2 py-1.5 text-xs border border-slate-300 focus:border-[#F97316] focus:outline-none">
            <option value="">All branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.branch_code} · {b.branch_name}</option>)}
          </select>
        </div>

        <div className="flex gap-2 pt-1 border-t border-slate-200">
          <button data-testid="run-preview-btn"
            onClick={runPreview} disabled={!canRun || busy}
            className="bg-white border border-slate-400 text-slate-800 hover:bg-slate-50 px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
            <Eye size={13} /> {busy ? "Loading…" : "Preview Rows"}
          </button>
          <button data-testid="download-pdf-btn"
            onClick={downloadPdf} disabled={!canRun || busy}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2 text-xs uppercase tracking-wider font-bold flex items-center gap-2 disabled:opacity-50">
            <Printer size={13} /> Download PDF
          </button>
        </div>
      </Card>

      {/* Preview pane */}
      {preview && (
        <Card data-testid="preview-card">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
                {kind === "material" ? "Material" : "Asset"} Issued to
              </div>
              <h2 className="text-base font-bold text-slate-800">
                {preview.party?.name}
                {preview.party?.code && <span className="text-slate-400 font-mono text-sm ml-2">{preview.party.code}</span>}
              </h2>
            </div>
            <FileText size={20} className="text-slate-400" />
          </div>
          {totalsRow}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">SL</th>
                  <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Voucher #</th>
                  <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Date</th>
                  {kind === "material" ? (
                    <>
                      <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Item Code</th>
                      <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Description</th>
                      <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Project</th>
                      <th className="text-center py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Unit</th>
                      <th className="text-right py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Qty</th>
                      <th className="text-right py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Rate</th>
                    </>
                  ) : (
                    <>
                      <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Asset Code</th>
                      <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Asset Name</th>
                      <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Project</th>
                      <th className="text-center py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Type</th>
                      <th className="text-center py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Exp. Return</th>
                    </>
                  )}
                  <th className="text-right py-2 px-3 text-[10px] uppercase tracking-wider font-bold text-slate-500">Value</th>
                </tr>
              </thead>
              <tbody>
                {(preview.rows || []).length === 0 && (
                  <tr><td colSpan={kind === "material" ? 10 : 9} className="text-center py-8 text-slate-500 italic">
                    No {kind} entries found for this {partyType.toLowerCase()} in the selected period.
                  </td></tr>
                )}
                {(preview.rows || []).map(r => (
                  <tr key={r.sl_no} className="border-b border-slate-100 hover:bg-slate-50/50">
                    <td className="py-1.5 px-3 text-slate-500">{r.sl_no}</td>
                    <td className="py-1.5 px-3 font-mono">{r.voucher_number}</td>
                    <td className="py-1.5 px-3">{r.voucher_date}</td>
                    {kind === "material" ? (
                      <>
                        <td className="py-1.5 px-3 font-mono">{r.item_code}</td>
                        <td className="py-1.5 px-3">{r.item_name || r.description}</td>
                        <td className="py-1.5 px-3">{r.project_name || "—"}</td>
                        <td className="py-1.5 px-3 text-center">{r.unit}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums">{fmtINR(r.quantity)}</td>
                        <td className="py-1.5 px-3 text-right tabular-nums">{fmtINR(r.unit_rate)}</td>
                      </>
                    ) : (
                      <>
                        <td className="py-1.5 px-3 font-mono">{r.item_code}</td>
                        <td className="py-1.5 px-3">{r.item_name}</td>
                        <td className="py-1.5 px-3">{r.project_name || "—"}</td>
                        <td className="py-1.5 px-3 text-center">{r.issue_type || "—"}</td>
                        <td className="py-1.5 px-3 text-center">{r.expected_return_date || "—"}</td>
                      </>
                    )}
                    <td className="py-1.5 px-3 text-right tabular-nums font-medium">{fmtINR(r.line_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
